import {
  Snackbar,
  Alert,
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  Button,
  Divider,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";
import usePageTitle from "app/pageTitle";

import MDInput from "theme/components/MDInput";
import MDButton from "theme/components/MDButton";
import DataTable from "theme/Tables/DataTable";
import axios from "axios.js";
import { useAwaitDialog } from "app/components/status_dialog";
import GoogleMapReact from "google-map-react";
import AppConfig from "appConfig";

function VisitTrackerVisitLogsPage() {
  const pageTitle = "Visit Tracker - Visit Logs";
  usePageTitle(pageTitle);
  const { ref } = useParams();
  const navigate = useNavigate();

  const statusDialog = useAwaitDialog();
  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };
  const [refreshPage, setRefreshPage] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);

  const [visits, setVisits] = useState([]);
  const [visit, setVisit] = useState(null);

  const [resultData, setResultData] = useState([]);
  const [mapZoom, setMapZoom] = useState(11);

  const visitsTableColumns = [
    { Header: "Actions", accessor: "actions", align: "left" },
    { Header: "Location", accessor: "location", align: "left" },
    { Header: "Agent", accessor: "agent", align: "left" },
    { Header: "Location Group", accessor: "group", align: "left" },
    { Header: "State", accessor: "state", align: "left" },
    { Header: "LGA", accessor: "lga", align: "left" },
    { Header: "Date", accessor: "created", align: "right" },
  ];

  const buildRows = (data) => {
    const rows = [];
    data.map((row) => {
      rows.push({
        location: row.location.title,
        agent: row.agent.title,
        group: row.location.group,
        state: row.state,
        lga: row.lga,
        actions: (
          <MDBox mt={1} variant="button" display="flex" gap={0.5}>
            <Tooltip title="View Check In">
              <MDButton
                size="small"
                variant="outlined"
                color="info"
                iconOnly
                onClick={() => {
                  getVisit(row.reference);
                }}
              >
                <Icon sx={{ cursor: "pointer" }} fontSize="small">
                  visibility
                </Icon>
              </MDButton>
            </Tooltip>
          </MDBox>
        ),
        created: row.created,
      });
      return null;
    });

    return rows;
  };

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get(`/portal/visits/trackers/${ref}/visits/logs.json`)
        .then((result) => {
          // console.log(result);
          setShowLoading(false);
          if (result.data.status === "success") {
            setResultData(result.data.data);

            const rows = buildRows(result.data.data.logs);
            setVisits(rows);

            setPageLoaded(true);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine ? "" : "Make sure you have an active internet connection"
            }`
          );
          setOpen(true);
        });
    };

    fetchData();
  }, [refreshPage]);

  async function getVisit(visitRef) {
    setShowLoading(true);
    await axios
      .get(`/portal/visits/trackers/visits/logs/${visitRef}/fetch.json`)
      .then((result) => {
        // console.log(result);
        setShowLoading(false);
        if (result.data.status === "success") {
          setVisit(result.data.data.visit);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>

      {visit && (
        <Dialog open fullWidth maxWidth="sm">
          <DialogTitle>Check In</DialogTitle>
          <DialogContent>
            <MDBox>
              <MDBox
                mt={1}
                p={2}
                mb={3}
                borderRadius="lg"
                sx={{
                  background: "#fafafa",
                  border: "1px solid #ececec",
                }}
              >
                <MDTypography variant="caption" color="text">
                  LOCATION
                </MDTypography>
                <MDTypography variant="h5" fontWeight="bold">
                  {visit.location.title}
                </MDTypography>
              </MDBox>
              <MDBox
                p={2}
                mt={2}
                borderRadius="lg"
                sx={{
                  border: "1px solid #ececec",
                }}
              >
                <MDBox display="flex" justifyContent="space-between">
                  <MDTypography variant="button" color="text" fontWeight="regular">
                    Agent
                  </MDTypography>
                  <MDTypography variant="button" color="text">
                    {visit.agent}
                  </MDTypography>
                </MDBox>
                <Divider />
                <MDBox mt={2} display="flex" justifyContent="space-between">
                  <MDTypography variant="button" color="text" fontWeight="regular">
                    Date
                  </MDTypography>
                  <MDTypography variant="button" color="text">
                    {visit.created}
                  </MDTypography>
                </MDBox>
              </MDBox>
              <MDBox
                p={2}
                mt={2}
                borderRadius="lg"
                sx={{
                  border: "1px solid #ececec",
                }}
              >
                <MDBox display="flex" justifyContent="space-between">
                  <MDTypography variant="button" color="text" fontWeight="regular">
                    GPS
                  </MDTypography>
                  <MDButton
                    color="error"
                    variant="outlined"
                    iconOnly
                    size="small"
                    onClick={() => {
                      window.open(
                        `https://maps.google.com?q=${visit.latitude},${visit.longitude}`,
                        true
                      );
                    }}
                  >
                    <Icon>location_on</Icon>
                  </MDButton>
                </MDBox>
              </MDBox>
              {visit.photo && (
                <MDBox
                  p={2}
                  mt={3}
                  borderRadius="lg"
                  sx={{
                    background: "#fafafa",
                    border: "1px solid #ececec",
                  }}
                  display="flex"
                  justifyContent="space-between"
                >
                  <img src={visit.photo} width="100%" />
                </MDBox>
              )}
            </MDBox>
          </DialogContent>
          <DialogActions>
            <MDButton
              variant="outlined"
              color="dark"
              startIcon={<Icon>close</Icon>}
              onClick={() => setVisit(null)}
            >
              Close
            </MDButton>
          </DialogActions>
        </Dialog>
      )}

      <MDBox pb={3}>
        {pageLoaded && (
          <MDBox>
            <MDBox mt={1}>
              <Button
                color="dark"
                startIcon={<Icon>west</Icon>}
                onClick={() => {
                  navigate(`/visits/trackers/${ref}`);
                }}
              >
                Back to tracker
              </Button>
            </MDBox>
            {resultData.logs.length > 0 && (
              <MDBox mt={1.5}>
                <Card>
                  <MDBox p={3}>
                    <MDBox mb={3} display="flex" justifyContent="space-between">
                      <MDBox>
                        <MDTypography variant="h6" fontWeight="bold">
                          Check-In Heat Map
                        </MDTypography>
                        <MDTypography variant="h6" fontWeight="light">
                          Recent team's recorded check-ins
                        </MDTypography>
                      </MDBox>
                      <MDBox>
                        <MDButton
                          size="small"
                          variant="outlined"
                          color="dark"
                          iconOnly
                          onClick={() => {
                            let pZoom = mapZoom;
                            pZoom -= 1;
                            pZoom = pZoom < 4 ? 4 : pZoom;
                            setMapZoom(pZoom);
                          }}
                        >
                          <Icon>remove</Icon>
                        </MDButton>
                        &nbsp;
                        <MDTypography variant="caption" fontSize={10} fontWeight="light">
                          Zoom
                        </MDTypography>
                        &nbsp;
                        <MDButton
                          size="small"
                          variant="outlined"
                          color="dark"
                          iconOnly
                          onClick={() => {
                            let pZoom = mapZoom;
                            pZoom += 1;
                            pZoom = pZoom > 20 ? 20 : pZoom;
                            setMapZoom(pZoom);
                          }}
                        >
                          <Icon>add</Icon>
                        </MDButton>
                      </MDBox>
                    </MDBox>
                    <MDBox sx={{ height: "55vh", width: "100%" }}>
                      <GoogleMapReact
                        bootstrapURLKeys={{ key: AppConfig.google_map_key }}
                        defaultCenter={{
                          lat: parseFloat(resultData?.logs[0]?.latitude),
                          lng: parseFloat(resultData?.logs[0]?.longitude),
                        }}
                        defaultZoom={mapZoom}
                        zoom={mapZoom}
                      >
                        {resultData.logs.map((loc) => (
                          <MDBox
                            lat={parseFloat(loc.latitude)}
                            lng={parseFloat(loc.longitude)}
                            // onClick={() => navigate(`/data-capture/${loc.reference}`)}
                            onClick={() => {
                              window.open(
                                `https://maps.google.com?q=${loc.latitude},${loc.longitude}`,
                                true
                              );
                            }}
                          >
                            <Tooltip
                              title={
                                <>
                                  <MDTypography color="white" variant="h6" fontSize={12}>
                                    {loc.agent.title}
                                  </MDTypography>
                                  <MDTypography
                                    color="white"
                                    variant="h6"
                                    fontSize={12}
                                    fontWeight="regular"
                                  >
                                    {loc.location.title}
                                  </MDTypography>
                                  <MDTypography color="white" variant="caption">
                                    {loc.created}
                                  </MDTypography>
                                </>
                              }
                            >
                              <MDTypography fontSize={32}>
                                <Icon color="error">person_pin_circle</Icon>
                              </MDTypography>
                            </Tooltip>
                          </MDBox>
                        ))}
                      </GoogleMapReact>
                    </MDBox>
                  </MDBox>
                </Card>
              </MDBox>
            )}

            <MDBox mt={5}>
              <Card sx={{ height: "100%", marginTop: "0px" }}>
                <MDBox>
                  <MDBox p={2}>
                    <MDBox display="flex" justifyContent="space-between" alignItems="center">
                      <MDBox>
                        <MDTypography variant="h6" fontWeight="medium" textTransform="capitalize">
                          Check-Ins
                        </MDTypography>
                      </MDBox>
                    </MDBox>
                  </MDBox>
                  <MDBox mt={0}>
                    <DataTable
                      table={{ columns: visitsTableColumns, rows: visits }}
                      isSorted
                      canSearch
                      entriesPerPage
                      showTotalEntries
                      buttons
                      noEndBorder
                    />
                  </MDBox>
                </MDBox>
              </Card>
            </MDBox>
          </MDBox>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default VisitTrackerVisitLogsPage;
